import * as React from "react";
import { ArrowRight, Check, Clock, Loader2, MapPin, MoreHorizontal, Truck } from "lucide-react";
import { apiFetch } from "@/lib/api.js";
import { useToast } from "@/components/Toast.js";
import { cn } from "@/lib/utils.js";

/**
 * "Moves to make" — planned moves grouped by destination
 * (docs/organize-declutter-onboarding-design.md §6b).
 *
 * A planned move is decided but not yet done: the item is still where it was.
 * Groups are per destination ("Kitchen: 4 things") because people carry things
 * room to room. Each group is confirmed at once ("Moved all"), with single items
 * untickable. A move planned more than 7 days ago reads "Still to move" and
 * offers: Moved it · Decide again (→ Re-locate) · Drop it (stays where it is).
 *
 * Used after a sort (filtered to that sort's items), on /organize/moves, and as
 * the target of the hub's "Needs you now" row.
 */

export interface PlannedMoveItem {
  id: string;
  name: string;
  photoUrl: string | null;
  quantity: number;
  fromSpaceId: string | null;
  fromSpaceName: string | null;
  fromSpacePath: string | null;
  plannedAt: string | null;
  stale: boolean;
}

export interface PlannedMoveGroup {
  spaceId: string;
  spaceName: string;
  spacePath: string;
  items: PlannedMoveItem[];
}

export interface PlannedMovesResponse {
  groups: PlannedMoveGroup[];
  total: number;
  staleCount: number;
}

interface Props {
  /** Only show these items (e.g. the items planned by the sort that just finished). */
  itemIds?: string[];
  /** Called after any change, with the remaining count in view. */
  onChange?: (remaining: number) => void;
  /** Shown when there's nothing to move. Omit to render nothing. */
  emptyText?: string;
}

const thing = (n: number) => `${n} thing${n === 1 ? "" : "s"}`;

export function MovesToMake({ itemIds, onChange, emptyText }: Props) {
  const toast = useToast();
  const [data, setData] = React.useState<PlannedMovesResponse | null>(null);
  const [loadError, setLoadError] = React.useState(false);
  // Unticked items per group (default: everything ticked).
  const [unticked, setUnticked] = React.useState<Set<string>>(new Set());
  const [busyGroup, setBusyGroup] = React.useState<string | null>(null);
  const [busyItem, setBusyItem] = React.useState<string | null>(null);
  const [openItemMenu, setOpenItemMenu] = React.useState<string | null>(null);

  const filterKey = itemIds ? [...itemIds].sort().join(",") : "";
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;

  const load = React.useCallback(async () => {
    setLoadError(false);
    try {
      const res = await apiFetch<PlannedMovesResponse>("/api/items/planned-moves");
      const only = filterKey ? new Set(filterKey.split(",")) : null;
      const groups = only
        ? res.groups
            .map((g) => ({ ...g, items: g.items.filter((i) => only.has(i.id)) }))
            .filter((g) => g.items.length > 0)
        : res.groups;
      const total = groups.reduce((n, g) => n + g.items.length, 0);
      setData({ groups, total, staleCount: groups.reduce((n, g) => n + g.items.filter((i) => i.stale).length, 0) });
      onChangeRef.current?.(total);
    } catch {
      setLoadError(true);
    }
  }, [filterKey]);

  React.useEffect(() => { load(); }, [load]);

  const confirm = async (ids: string[], destName: string) => {
    const res = await apiFetch<{ moved: number }>("/api/items/planned-moves/confirm", {
      method: "POST",
      body: JSON.stringify({ itemIds: ids }),
    });
    toast.show(`Moved ${thing(res.moved)} to ${destName}`, "success");
  };

  const confirmGroup = async (group: PlannedMoveGroup) => {
    const ids = group.items.filter((i) => !unticked.has(i.id)).map((i) => i.id);
    if (ids.length === 0 || busyGroup) return;
    setBusyGroup(group.spaceId);
    try {
      await confirm(ids, group.spaceName);
      await load();
    } catch (err: any) {
      toast.show(err?.message || "Couldn't record the move", "error");
    }
    setBusyGroup(null);
  };

  const itemAction = async (item: PlannedMoveItem, group: PlannedMoveGroup, action: "confirm" | "decide-again" | "drop") => {
    if (busyItem) return;
    setBusyItem(item.id);
    setOpenItemMenu(null);
    try {
      if (action === "confirm") {
        await confirm([item.id], group.spaceName);
      } else {
        await apiFetch(`/api/items/planned-moves/${action}`, { method: "POST", body: JSON.stringify({ itemIds: [item.id] }) });
        toast.show(
          action === "decide-again" ? `${item.name} is in Re-locate — decide where it goes later.` : `${item.name} stays where it is.`,
          "success",
        );
      }
      await load();
    } catch (err: any) {
      toast.show(err?.message || "Something went wrong", "error");
    }
    setBusyItem(null);
  };

  const toggle = (id: string) => setUnticked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  if (loadError) {
    return (
      <div className="p-4 rounded-lg border border-clay-border bg-white text-sm text-body flex items-center justify-between gap-3">
        <span>Couldn't load your moves.</span>
        <button onClick={load} className="text-sm font-medium text-primary hover:underline">Retry</button>
      </div>
    );
  }
  if (!data) {
    return <div className="h-24 bg-clay-muted rounded-lg animate-pulse" />;
  }
  if (data.groups.length === 0) {
    return emptyText ? (
      <div className="p-5 rounded-lg border border-clay-border bg-sage-wash text-center text-sm text-body">{emptyText}</div>
    ) : null;
  }

  return (
    <div className="space-y-3">
      {data.groups.map((group) => {
        const ticked = group.items.filter((i) => !unticked.has(i.id)).length;
        const all = ticked === group.items.length;
        return (
          <section key={group.spaceId} className="bg-white rounded-lg border border-clay-border p-4 space-y-3">
            <header className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-md bg-amethyst-soft flex items-center justify-center shrink-0">
                <MapPin className="w-4 h-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-[1.0625rem] font-semibold text-ink truncate">{group.spacePath || group.spaceName}</h3>
                <p className="text-[0.8125rem] text-faint tabular-nums">{thing(group.items.length)} to take here</p>
              </div>
            </header>

            <ul className="space-y-1">
              {group.items.map((item) => {
                const on = !unticked.has(item.id);
                return (
                  <li key={item.id} className="rounded-md hover:bg-paper-raised">
                    <div className="flex items-center gap-2.5 px-2 py-2">
                      <button
                        type="button"
                        role="checkbox"
                        aria-checked={on}
                        aria-label={`Include ${item.name}`}
                        onClick={() => toggle(item.id)}
                        className={cn(
                          "w-5 h-5 rounded-sm border flex items-center justify-center shrink-0 transition-colors",
                          on ? "bg-primary border-primary" : "bg-white border-clay-border",
                        )}
                      >
                        {on && <Check className="w-3.5 h-3.5 text-white" />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-ink truncate">
                          {item.name}
                          {item.quantity > 1 && <span className="text-faint font-normal tabular-nums"> ×{item.quantity}</span>}
                        </p>
                        <p className="text-[0.8125rem] text-faint truncate inline-flex items-center gap-1">
                          {item.fromSpacePath ?? item.fromSpaceName ?? "No location"}
                          <ArrowRight className="w-3 h-3 shrink-0" />
                          {group.spaceName}
                        </p>
                      </div>
                      {item.stale && (
                        <span className="text-[0.75rem] font-medium text-warning-text bg-warning-wash rounded-pill px-2 py-0.5 shrink-0 inline-flex items-center gap-1">
                          <Clock className="w-3 h-3" />Still to move
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => setOpenItemMenu((cur) => (cur === item.id ? null : item.id))}
                        aria-label={`More options for ${item.name}`}
                        aria-expanded={openItemMenu === item.id || item.stale}
                        className={cn("p-1.5 rounded-sm text-faint hover:text-body shrink-0", item.stale && "hidden")}
                      >
                        {busyItem === item.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <MoreHorizontal className="w-4 h-4" />}
                      </button>
                    </div>
                    {(item.stale || openItemMenu === item.id) && (
                      <div className="flex flex-wrap gap-2 px-2 pb-2 pl-9">
                        <button
                          onClick={() => itemAction(item, group, "confirm")}
                          disabled={busyItem === item.id}
                          className="px-2.5 py-1 rounded-sm bg-sage-wash text-sage-deep text-[0.8125rem] font-medium hover:bg-sage-wash/70 disabled:opacity-50"
                        >
                          Moved it
                        </button>
                        <button
                          onClick={() => itemAction(item, group, "decide-again")}
                          disabled={busyItem === item.id}
                          className="px-2.5 py-1 rounded-sm bg-clay-muted text-body text-[0.8125rem] font-medium hover:bg-clay-muted/70 disabled:opacity-50"
                        >
                          Decide again
                        </button>
                        <button
                          onClick={() => itemAction(item, group, "drop")}
                          disabled={busyItem === item.id}
                          className="px-2.5 py-1 rounded-sm text-faint text-[0.8125rem] font-medium hover:text-body disabled:opacity-50"
                        >
                          Drop it
                        </button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>

            <button
              onClick={() => confirmGroup(group)}
              disabled={ticked === 0 || busyGroup === group.spaceId}
              className="w-full py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed inline-flex items-center justify-center gap-1.5"
            >
              {busyGroup === group.spaceId ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Truck className="w-4 h-4" />
              )}
              {all ? "Moved all" : `Moved ${thing(ticked)}`}
            </button>
          </section>
        );
      })}
    </div>
  );
}
